import React, { useState } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Space,
  Avatar,
  Typography,
  Row,
  Col,
  Divider,
  Badge,
  notification,
  Descriptions,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  EditOutlined,
  SaveOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useEditarPerfil } from '../../../Hooks/Player';

const { Title, Text } = Typography;

export const Perfil = () => {
  const [activeTabKey, setActiveTabKey] = useState('info');

  const storedDataUser = JSON.parse(localStorage.getItem('dataUser'));
  const apiEditarPerfil = useEditarPerfil();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);

  const onFinish = async (values) => {
    try {
      await apiEditarPerfil.mutateAsync({
        idEntrenador: storedDataUser.id,
        usua_nombre: values.usua_nombre,
        usua_correo: values.usua_correo,
        usua_password: values.usua_password,
      });

      notification.success({
        message: 'Perfil actualizado',
        description: 'Los cambios se han guardado correctamente',
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      });

      setIsEditing(false);
      setActiveTabKey('info');

      const updatedUser = {
        ...storedDataUser,
        usua_nombre: values.usua_nombre,
        usua_correo: values.usua_correo,
      };
      localStorage.setItem('dataUser', JSON.stringify(updatedUser));
    } catch (error) {
      notification.error({
        message: 'Error al actualizar',
        description: 'No se pudieron guardar los cambios',
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setActiveTabKey('editar');
  };

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
    setActiveTabKey('info');
  };

  const InfoTab = () => (
    <Row gutter={[24, 16]}>
      <Col span={24}>
        <div style={{ marginBottom: 24 }}>
          <Card
            style={{
              borderRadius: '12px',
              border: '1px solid #f0f0f0',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Row align="middle" gutter={24}>
              <Col>
                <Badge count={<CameraOutlined style={{ color: '#1890ff' }} />} offset={[-10, 10]}>
                  <Avatar
                    size={80}
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: '#1890ff',
                      border: '3px solid #e6f7ff',
                    }}
                  />
                </Badge>
              </Col>
              <Col flex={1}>
                <Title level={3} style={{ margin: 0 }}>
                  {storedDataUser.usua_nombre}
                </Title>
                <Text type="secondary">{storedDataUser.usua_correo}</Text>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  Editar Perfil
                </Button>
              </Col>
            </Row>
          </Card>
        </div>

        <Card
          title={
            <span>
              <UserOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              Información del Perfil
            </span>
          }
          style={{
            borderRadius: '12px',
            border: '1px solid #f0f0f0',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Nombre completo">
              {storedDataUser.usua_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Correo electrónico">
              {storedDataUser.usua_correo}
            </Descriptions.Item>
            <Descriptions.Item label="ID de Usuario">
              {storedDataUser.id}
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              <Badge status="success" text="Activo" />
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
    </Row>
  );

  const EditTab = () => (
    <Card
      title={
        <span>
          <EditOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          Editar Perfil
        </span>
      }
      style={{
        borderRadius: '12px',
        border: '1px solid #f0f0f0',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{
          usua_nombre: storedDataUser.usua_nombre,
          usua_correo: storedDataUser.usua_correo,
        }}
        size="large"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Nombre completo"
              name="usua_nombre"
              rules={[{ required: true, message: 'El nombre es requerido' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                placeholder="Ingrese su nombre completo"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Correo electrónico"
              name="usua_correo"
              rules={[
                { required: true, message: 'El correo es requerido' },
                { type: 'email', message: 'Ingrese un correo válido' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#1890ff' }} />}
                placeholder="Ingrese su correo electrónico"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">
          <Text strong style={{ color: '#666' }}>
            Cambiar Contraseña (Opcional)
          </Text>
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Nueva Contraseña" name="usua_password">
              <Input.Password
                prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                placeholder="Ingrese nueva contraseña"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Confirmar Contraseña"
              name="confirm"
              dependencies={['usua_password']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const password = getFieldValue('usua_password');
                    if (!password) {
                      return Promise.resolve();
                    }
                    if (!value) {
                      return Promise.reject(new Error('Confirme su contraseña'));
                    }
                    if (password !== value) {
                      return Promise.reject(new Error('Las contraseñas no coinciden'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                placeholder="Confirme su nueva contraseña"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginTop: '32px', marginBottom: 0 }}>
          <Space size="middle">
            <Button
              type="primary"
              htmlType="submit"
              loading={apiEditarPerfil.isLoading}
              icon={<SaveOutlined />}
            >
              Guardar Cambios
            </Button>
            <Button onClick={handleCancel}>
              Cancelar
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );

  const items = [
    {
      key: 'info',
      label: (
        <span>
          <UserOutlined style={{ marginRight: '8px' }} />
          Información
        </span>
      ),
      children: <InfoTab />,
    },
    {
      key: 'editar',
      label: (
        <span>
          <SafetyCertificateOutlined style={{ marginRight: '8px' }} />
          Editar
        </span>
      ),
      children: <EditTab />,
    },
  ];

  return (
    <Card style={{ width: '100%' }}>
      <Tabs
        items={items}
        activeKey={activeTabKey}
        onChange={(key) => {
          setActiveTabKey(key);
          if (key === 'info') setIsEditing(false);
        }}
        tabProps={{ size: 'small' }}
      />
    </Card>
  );
};
